import { PhotoMetadataRecord } from "@/lib/catalog/types"
import { Adjustments } from "./types"
import { Renderer } from "./pipeline/renderer"

export interface IOAdapterOptions {
	signal?: AbortSignal

	photoId?: string
	adjustments?: Adjustments
	metadata?: PhotoMetadataRecord

	enableDenoise?: boolean
	enableHalfResolution?: boolean

	skipRenderOnLoad?: boolean
	skipHistogram?: boolean
}

export interface PreparedIOAdapterImport {
	readonly file: File
	/** Pass this exact object to Editor.import to consume the preparation. */
	readonly options: IOAdapterOptions
	/** Releases prepared resources when the import will not be consumed. */
	dispose(): Promise<void>
}

type PreparedIOAdapterImportState = "prepared" | "committing" | "settled"

type CommitPreparedIOAdapterImport = (
	renderer: Renderer,
	options: IOAdapterOptions,
) => Promise<void> | void

const preparedImports = new WeakMap<IOAdapterOptions, PreparedIOAdapterImportImpl>()

class PreparedIOAdapterImportImpl implements PreparedIOAdapterImport {
	private state: PreparedIOAdapterImportState = "prepared"
	private consumePromise?: Promise<void>
	private disposePromise?: Promise<void>

	constructor(
		private readonly owner: IOAdapter,
		readonly file: File,
		readonly options: IOAdapterOptions,
		private readonly commit: CommitPreparedIOAdapterImport,
		private readonly release: () => Promise<void> | void,
	) {}

	consume(owner: IOAdapter, file: File, renderer: Renderer) {
		if (owner !== this.owner) {
			throw new Error("Prepared import belongs to a different IO adapter")
		}
		if (file !== this.file) {
			throw new Error("Prepared import belongs to a different file")
		}
		if (this.state !== "prepared") {
			throw new Error(`Prepared import is already ${this.state}`)
		}

		this.state = "committing"
		this.consumePromise = Promise.resolve()
			.then(() => this.commit(renderer, this.options))
			.finally(async () => {
				this.state = "settled"
				await this.releasePreparedResources()
			})

		return this.consumePromise
	}

	async dispose() {
		if (this.state === "committing") {
			try {
				await this.consumePromise
			} catch {
				// The consumer reports commit failures; dispose only reports cleanup failures.
			}
		} else if (this.state === "prepared") {
			this.state = "settled"
		}

		await this.releasePreparedResources()
	}

	private releasePreparedResources() {
		this.disposePromise ??= Promise.resolve().then(this.release)
		return this.disposePromise
	}
}

export abstract class IOAdapter {
	public abstract getName(): string

	public abstract canLoad(file: File): boolean
	public abstract load(file: File, renderer: Renderer, options: IOAdapterOptions): Promise<void>

	public prepare(file: File, options: IOAdapterOptions): Promise<PreparedIOAdapterImport> {
		return Promise.resolve({
			file,
			options: { ...options },
			dispose: () => Promise.resolve(),
		})
	}

	protected createPreparedImport(
		file: File,
		options: IOAdapterOptions,
		commit: CommitPreparedIOAdapterImport,
		release: () => Promise<void> | void,
	): PreparedIOAdapterImport {
		const preparedOptions = { ...options }
		const preparedImport = new PreparedIOAdapterImportImpl(
			this,
			file,
			preparedOptions,
			commit,
			release,
		)
		preparedImports.set(preparedOptions, preparedImport)
		return preparedImport
	}

	protected consumePreparedImport(file: File, renderer: Renderer, options: IOAdapterOptions) {
		return preparedImports.get(options)?.consume(this, file, renderer)
	}
}
